import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaginationProps } from 'antd';
import { Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useViewClaimList, type ViewClaimFilters } from 'all-in-one-shared-api';
import { useKeycloak } from 'all-in-one-react-keycloak';

import { isLocaleEnglish } from 'utils/languageUtils';
import { TitleRow } from 'components/styledComponents';
import { NewCustomPagination, Spinner } from 'components';
import { StyledTitle } from 'components/StyledTitle';

import formatClaimCardData from '../../utils/formatClaimCardData';
import { ClaimCard, FilterSection } from '../';
import { RowSpaced } from '../styledComponents';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { useCachedClaimProperties } from 'hooks';

const PAGE_SIZE = 6;

export const MyClaims = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [inited, setInited] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);
  const { keycloak } = useKeycloak();
  const [claimsQuery, setClaimQuery] = useState<ViewClaimFilters>({});

  const { shouldFetch, data, isLoading } = useViewClaimList(
    { name: 'useViewClaimList', params: claimsQuery },
    keycloak.token || '',
    claimsQuery,
    { shouldRetryOnError: false },
  );

  useEffect(() => {
    setClaimQuery((prevState) => ({
      ...prevState,
      patientMemberTypes: claimProperties?.familyMembers?.map((a) => a.memberType || '0'),
    }));
  }, [claimProperties]);

  useEffect(() => {
    if (keycloak.authenticated && claimsQuery.patientMemberTypes) shouldFetch(true);

    if (!inited && keycloak.authenticated) setInited(true);
  }, [inited, keycloak.authenticated, shouldFetch, claimsQuery.patientMemberTypes]);

  useEffect(() => {
    setPage(1);
  }, [claimsQuery]);

  const claims = useMemo(() => {
    if (!data) return [];

    return data.results.map(formatClaimCardData(isLocaleEnglish(i18n.language)));
  }, [data, i18n.language]);

  const totalCount = data?.totalCount || 0;

  const claimListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    claimListRef.current?.scrollIntoView();
  }, [page]);

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (_, pageSize: number) => {
    setPageSize(pageSize);
  };

  const ClaimList = (): JSX.Element => {
    // Note(Anson Kan): to prevent showing the error before start fetching
    if (!inited || isLoading) return <Spinner />;

    if (totalCount === 0) return <div>{t('claims.noClaimsFound')}</div>;

    return (
      <>
        {/* TODO(Anson Kan): abstract these spacing properties */}
        <RowSpaced gutter={[16, 16]} wrap>
          {claims
            .slice((page - 1) * pageSize, page * pageSize)
            .map(({ claimId, referenceNo, sequenceNo, ...props }) => (
              // TODO(Anson Kan): abstract these responsive properties
              <Col key={`${claimId}-${referenceNo}-${sequenceNo}`} xs={24} sm={12} lg={8}>
                <ClaimCard claimId={claimId} referenceNo={referenceNo} sequenceNo={sequenceNo} {...props} />
              </Col>
            ))}
        </RowSpaced>
        <RowSpaced>
          <Col>
            <NewCustomPagination
              current={page}
              onChange={setPage}
              total={totalCount}
              defaultPageSize={pageSize}
              pageSizeOptions={[PAGE_SIZE, 10, 20, 50, 100]}
              onShowSizeChange={onShowSizeChange}
            />
          </Col>
        </RowSpaced>
      </>
    );
  };

  return (
    <div ref={claimListRef}>
      <TitleRow>
        <Col>
          <StyledTitle color="var(--text-color-secondary)" level={3}>
            {t('myClaims.title')}
          </StyledTitle>
        </Col>
      </TitleRow>

      <FilterSection
        claimsQuery={claimsQuery}
        setClaimQuery={setClaimQuery}
        token={keycloak.token ?? ''}
        totalCount={totalCount}
      />

      <ClaimList />
    </div>
  );
};
